import type {
  TextOverlayZoneDto,
  TextOverlayDtoStyle,
  ImageFormatResponseDto,
  GenerateImageResponseDto,
  ProductVariantSearchResultDto,
} from '@/apis/backend/generated/models';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useOrganization } from '@/context/organization-context';
import { ProductPickerModal } from '@/components/email-generation/ProductPickerModal';
import {
  useSmartImageGenControllerGenerate,
  useSmartImageGenControllerListJobs,
  useSmartImageGenControllerListFormats,
} from '@/apis/backend/generated/smart-image-gen/smart-image-gen';

import {
  Box,
  Chip,
  Stack,
  Paper,
  Alert,
  alpha,
  Button,
  Switch,
  Select,
  Avatar,
  Tooltip,
  MenuItem,
  Skeleton,
  Snackbar,
  useTheme,
  TextField,
  Typography,
  InputLabel,
  IconButton,
  FormControl,
  ListSubheader,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Image as ImageIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  Layers as LayersIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  HourglassEmpty as PendingIcon,
  AutoAwesome as AutoAwesomeIcon,
  CheckCircle as CheckCircleIcon,
  ShoppingBag as ShoppingBagIcon,
} from '@mui/icons-material';

import { DashboardContent } from 'src/layouts/dashboard';

export const Route = createFileRoute('/_authed/dashboard/o/$orgId/c/$clientId/image-generator/')({
  component: ImageGeneratorPage,
});

function ImageGeneratorPage() {
  const { t } = useTranslation('common');
  const theme = useTheme();

  const IMAGE_TYPES = [
    { value: 'hero', label: t('imageGenerator.imageTypes.hero', 'Hero Banner') },
    { value: 'product', label: t('imageGenerator.imageTypes.product', 'Product') },
    { value: 'lifestyle', label: t('imageGenerator.imageTypes.lifestyle', 'Lifestyle') },
    { value: 'social', label: t('imageGenerator.imageTypes.social', 'Social Ad') },
    { value: 'feature', label: t('imageGenerator.imageTypes.feature', 'Feature') },
    { value: 'banner', label: t('imageGenerator.imageTypes.banner', 'Banner') },
    { value: 'background', label: t('imageGenerator.imageTypes.background', 'Background') },
  ];

  const CONTENT_OPTIONS = [
    { value: 'heading', label: t('imageGenerator.contentOptions.heading', 'Heading') },
    { value: 'subheading', label: t('imageGenerator.contentOptions.subheading', 'Subheading') },
    { value: 'body-text', label: t('imageGenerator.contentOptions.bodyText', 'Body text') },
    { value: 'cta', label: t('imageGenerator.contentOptions.cta', 'Button / CTA') },
    { value: 'logo', label: t('imageGenerator.contentOptions.logo', 'Logo') },
  ];
  const queryClient = useQueryClient();
  const { activeOrganization, activeClient } = useOrganization();
  const orgId = activeOrganization?.id || '';
  const clientId = activeClient?.id || '';

  // Form state
  const [prompt, setPrompt] = useState('');
  const [imageType, setImageType] = useState('hero');
  const [format, setFormat] = useState('');
  const [textOverlayEnabled, setTextOverlayEnabled] = useState(false);
  const [textOverlayZones, setTextOverlayZones] = useState<TextOverlayZoneDto[]>([]);
  const [textOverlayStyle, setTextOverlayStyle] = useState<TextOverlayDtoStyle | ''>('');
  const [transparentBg, setTransparentBg] = useState(false);
  const [autoSelectRefs, setAutoSelectRefs] = useState(true);

  // Product picker
  const [selectedProducts, setSelectedProducts] = useState<ProductVariantSearchResultDto[]>([]);
  const [productPickerOpen, setProductPickerOpen] = useState(false);

  // Results
  const [results, setResults] = useState<GenerateImageResponseDto[]>([]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // API hooks
  const formatsQuery = useSmartImageGenControllerListFormats(orgId, clientId, {
    query: { enabled: !!orgId && !!clientId },
  });

  const jobsQuery = useSmartImageGenControllerListJobs(orgId, clientId, { limit: '20' }, {
    query: { enabled: !!orgId && !!clientId },
  });

  const generateMutation = useSmartImageGenControllerGenerate({
    mutation: {
      onSuccess: (data) => {
        setResults((prev) => [data, ...prev]);
        setSnackbar({ open: true, message: t('imageGenerator.toast.generatedSuccess', 'Image generated successfully'), severity: 'success' });
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey[0];
            return typeof key === 'string' && key.includes('/smart-image-gen/');
          },
        });
      },
      onError: (err: any) => {
        setSnackbar({
          open: true,
          message: t('imageGenerator.toast.generationFailed', 'Generation failed: {{message}}', { message: err?.message || t('imageGenerator.toast.unknownError', 'Unknown error') }),
          severity: 'error',
        });
      },
    },
  });

  const handleGenerate = () => {
    if (!prompt.trim() || !orgId || !clientId) return;
    generateMutation.mutate({
      orgId,
      clientId,
      data: {
        prompt: prompt.trim(),
        imageType,
        format: format || undefined,
        transparentBg,
        autoSelectReferences: autoSelectRefs,
        productIds: selectedProducts.length > 0 ? selectedProducts.map((p) => p.productId) : undefined,
        textOverlay: textOverlayEnabled && textOverlayZones.length > 0
          ? {
              zones: textOverlayZones,
              ...(textOverlayStyle ? { style: textOverlayStyle } : {}),
            }
          : undefined,
      },
    });
  };

  // Group formats by category
  const groupedFormats = (formatsQuery.data?.formats || []).reduce<Record<string, ImageFormatResponseDto[]>>(
    (acc, fmt) => {
      const cat = fmt.category || 'other';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(fmt);
      return acc;
    },
    {},
  );

  const copyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    setSnackbar({ open: true, message: t('imageGenerator.toast.urlCopied', 'URL copied to clipboard'), severity: 'success' });
  };

  const statusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />;
      case 'failed':
        return <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />;
      case 'generating':
        return <CircularProgress size={14} />;
      default:
        return <PendingIcon sx={{ fontSize: 16, color: 'text.disabled' }} />;
    }
  };

  return (
    <DashboardContent maxWidth="xl">
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 22, color: 'primary.main' }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
              {t('imageGenerator.title', 'Smart Image Generator')}
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              {t('imageGenerator.subtitle', 'Brand-aware multimodal generation with product + reference images')}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3 }}>
        {/* Left column: Controls */}
        <Stack spacing={3}>
          {/* Prompt input */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'grey.200',
              bgcolor: 'background.paper',
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 2 }}>
              {t('imageGenerator.prompt.label', 'Prompt')}
            </Typography>
            <TextField
              multiline
              rows={4}
              fullWidth
              placeholder={t('imageGenerator.prompt.placeholder', "Describe the image you want to generate... e.g. 'lifestyle photo of white sneakers walking down a city crosswalk at golden hour'")}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleGenerate();
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: alpha(theme.palette.primary.main, 0.02),
                  borderRadius: '10px',
                  fontSize: 14,
                  '& fieldset': { borderColor: alpha(theme.palette.grey[400], 0.3) },
                  '&:hover fieldset': { borderColor: alpha(theme.palette.primary.main, 0.2) },
                  '&.Mui-focused fieldset': { borderColor: alpha(theme.palette.primary.main, 0.4), borderWidth: 1 },
                },
              }}
            />

            {/* Text Overlay */}
            <Box sx={{ mt: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={textOverlayEnabled}
                    onChange={(e) => {
                      setTextOverlayEnabled(e.target.checked);
                      if (e.target.checked && textOverlayZones.length === 0) {
                        setTextOverlayZones([{ position: 'top', textColor: 'dark', content: 'heading' }]);
                      }
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('imageGenerator.textOverlay.label', 'Text Overlay Zones')}
                  </Typography>
                }
              />
              <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: -0.5, mb: 1 }}>
                {t('imageGenerator.textOverlay.hint', 'Tell the AI where text/buttons will go so it keeps those areas clear')}
              </Typography>

              {textOverlayEnabled && (
                <Stack spacing={1}>
                  {textOverlayZones.map((zone, idx) => (
                    <Stack key={idx} direction="row" spacing={1} alignItems="center">
                      <FormControl size="small" sx={{ minWidth: 130 }}>
                        <Select
                          value={zone.position}
                          onChange={(e) => {
                            const updated = [...textOverlayZones];
                            updated[idx] = { ...updated[idx], position: e.target.value as TextOverlayZoneDto['position'] };
                            setTextOverlayZones(updated);
                          }}
                          sx={{ borderRadius: '8px', fontSize: 12 }}
                        >
                          {['top', 'center', 'bottom', 'left', 'right', 'top-left', 'top-right', 'bottom-left', 'bottom-right'].map((pos) => (
                            <MenuItem key={pos} value={pos} sx={{ fontSize: 12 }}>{pos}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 80 }}>
                        <Select
                          value={zone.textColor || ''}
                          displayEmpty
                          onChange={(e) => {
                            const updated = [...textOverlayZones];
                            updated[idx] = { ...updated[idx], textColor: (e.target.value || undefined) as TextOverlayZoneDto['textColor'] };
                            setTextOverlayZones(updated);
                          }}
                          sx={{ borderRadius: '8px', fontSize: 12 }}
                        >
                          <MenuItem value="" sx={{ fontSize: 12 }}><em>{t('imageGenerator.textOverlay.colorAuto', 'Auto')}</em></MenuItem>
                          <MenuItem value="light" sx={{ fontSize: 12 }}>{t('imageGenerator.textOverlay.colorLight', 'Light')}</MenuItem>
                          <MenuItem value="dark" sx={{ fontSize: 12 }}>{t('imageGenerator.textOverlay.colorDark', 'Dark')}</MenuItem>
                        </Select>
                      </FormControl>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ flex: 1 }}>
                        {CONTENT_OPTIONS.map((opt) => {
                          const selected = (zone.content || '').split('+').filter(Boolean);
                          const isSelected = selected.includes(opt.value);
                          return (
                            <Chip
                              key={opt.value}
                              label={opt.label}
                              size="small"
                              variant={isSelected ? 'filled' : 'outlined'}
                              color={isSelected ? 'primary' : 'default'}
                              onClick={() => {
                                const updated = [...textOverlayZones];
                                const current = (updated[idx].content || '').split('+').filter(Boolean);
                                const next = isSelected
                                  ? current.filter((v) => v !== opt.value)
                                  : [...current, opt.value];
                                updated[idx] = { ...updated[idx], content: next.join('+') || undefined };
                                setTextOverlayZones(updated);
                              }}
                              sx={{ height: 24, fontSize: 11, fontWeight: 500, cursor: 'pointer' }}
                            />
                          );
                        })}
                      </Stack>
                      <IconButton
                        size="small"
                        onClick={() => setTextOverlayZones((prev) => prev.filter((_, i) => i !== idx))}
                        sx={{ color: 'text.disabled' }}
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Stack>
                  ))}

                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Button
                      size="small"
                      startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                      onClick={() => setTextOverlayZones((prev) => [...prev, { position: 'bottom', content: '' }])}
                      sx={{ borderRadius: '8px', textTransform: 'none', fontSize: 12, fontWeight: 500 }}
                    >
                      {t('imageGenerator.textOverlay.addZone', 'Add Zone')}
                    </Button>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <Select
                        value={textOverlayStyle}
                        displayEmpty
                        onChange={(e) => setTextOverlayStyle(e.target.value as TextOverlayDtoStyle | '')}
                        sx={{ borderRadius: '8px', fontSize: 12 }}
                      >
                        <MenuItem value="" sx={{ fontSize: 12 }}><em>{t('imageGenerator.textOverlay.styleAuto', 'Style: Auto')}</em></MenuItem>
                        <MenuItem value="photo-with-space" sx={{ fontSize: 12 }}>{t('imageGenerator.textOverlay.stylePhotoWithSpace', 'Photo with space')}</MenuItem>
                        <MenuItem value="product-on-solid" sx={{ fontSize: 12 }}>{t('imageGenerator.textOverlay.styleProductOnSolid', 'Product on solid')}</MenuItem>
                        <MenuItem value="product-cutout" sx={{ fontSize: 12 }}>{t('imageGenerator.textOverlay.styleProductCutout', 'Product cutout')}</MenuItem>
                        <MenuItem value="gradient-fade" sx={{ fontSize: 12 }}>{t('imageGenerator.textOverlay.styleGradientFade', 'Gradient fade')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                </Stack>
              )}
            </Box>

            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              {/* Image Type */}
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>{t('imageGenerator.fields.type', 'Type')}</InputLabel>
                <Select
                  value={imageType}
                  label={t('imageGenerator.fields.type', 'Type')}
                  onChange={(e) => setImageType(e.target.value)}
                  sx={{ borderRadius: '8px', fontSize: 13 }}
                >
                  {IMAGE_TYPES.map((imgType) => (
                    <MenuItem key={imgType.value} value={imgType.value} sx={{ fontSize: 13 }}>
                      {imgType.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Format */}
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>{t('imageGenerator.fields.format', 'Format')}</InputLabel>
                <Select
                  value={format}
                  label={t('imageGenerator.fields.format', 'Format')}
                  onChange={(e) => setFormat(e.target.value)}
                  sx={{ borderRadius: '8px', fontSize: 13 }}
                >
                  <MenuItem value="" sx={{ fontSize: 13 }}>
                    <em>{t('imageGenerator.fields.formatAuto', 'Auto (based on type)')}</em>
                  </MenuItem>
                  {Object.entries(groupedFormats).map(([category, formats]) => [
                    <ListSubheader key={category} sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: '32px' }}>
                      {category}
                    </ListSubheader>,
                    ...formats.map((f) => (
                      <MenuItem key={f.id} value={f.id} sx={{ fontSize: 13, pl: 3 }}>
                        {f.name}
                        {f.aspectRatio && (
                          <Typography component="span" sx={{ ml: 1, fontSize: 11, color: 'text.disabled' }}>
                            {f.aspectRatio}
                          </Typography>
                        )}
                      </MenuItem>
                    )),
                  ])}
                </Select>
              </FormControl>
            </Stack>

            <Stack direction="row" spacing={3} sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={transparentBg}
                    onChange={(e) => setTransparentBg(e.target.checked)}
                  />
                }
                label={<Typography sx={{ fontSize: 13 }}>{t('imageGenerator.fields.transparentBg', 'Transparent BG')}</Typography>}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={autoSelectRefs}
                    onChange={(e) => setAutoSelectRefs(e.target.checked)}
                  />
                }
                label={<Typography sx={{ fontSize: 13 }}>{t('imageGenerator.fields.autoSelectRefs', 'Auto-select references')}</Typography>}
              />
            </Stack>

            {/* Product Picker */}
            <Box sx={{ mt: 2.5 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
                {t('imageGenerator.products.label', 'Products (optional)')}
              </Typography>

              {selectedProducts.length > 0 && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                  {selectedProducts.map((p) => {
                    const imgUrl = p.variantImageUrl || p.productImages?.[0]?.src;
                    return (
                      <Chip
                        key={p.variantId}
                        label={p.productTitle}
                        size="small"
                        avatar={
                          imgUrl ? (
                            <Avatar src={imgUrl} sx={{ width: 20, height: 20 }} />
                          ) : (
                            <Avatar sx={{ width: 20, height: 20 }}><ShoppingBagIcon sx={{ fontSize: 12 }} /></Avatar>
                          )
                        }
                        onDelete={() => setSelectedProducts((prev) => prev.filter((sp) => sp.variantId !== p.variantId))}
                        sx={{ height: 26, fontSize: 12, fontWeight: 500, borderRadius: '6px' }}
                      />
                    );
                  })}
                </Stack>
              )}

              <Button
                variant="outlined"
                size="small"
                startIcon={<ShoppingBagIcon sx={{ fontSize: 16 }} />}
                onClick={() => setProductPickerOpen(true)}
                sx={{ borderRadius: '8px', textTransform: 'none', fontSize: 13, fontWeight: 500, borderColor: alpha(theme.palette.grey[400], 0.3) }}
              >
                {selectedProducts.length > 0 ? t('imageGenerator.products.change', 'Change Products') : t('imageGenerator.products.add', 'Add Products')}
              </Button>
            </Box>

            <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleGenerate}
                disabled={!prompt.trim() || generateMutation.isPending || !orgId || !clientId}
                startIcon={
                  generateMutation.isPending ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <AutoAwesomeIcon />
                  )
                }
                sx={{
                  px: 3,
                  py: 1,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: 14,
                  boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.25)}`,
                  '&:hover': {
                    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.35)}`,
                  },
                }}
              >
                {generateMutation.isPending ? t('imageGenerator.generate.loading', 'Generating...') : t('imageGenerator.generate.button', 'Generate')}
              </Button>
            </Box>

            {transparentBg && (
              <Typography sx={{ mt: 1.5, fontSize: 11, color: 'text.disabled', fontStyle: 'italic' }}>
                {t('imageGenerator.fields.transparentBgNote', 'Transparent background routes to DALL-E (better transparency results)')}
              </Typography>
            )}
          </Paper>

          {/* History */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'grey.200',
              bgcolor: 'background.paper',
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <HistoryIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {t('imageGenerator.history.title', 'Recent Jobs')}
                </Typography>
              </Stack>
              <IconButton size="small" onClick={() => jobsQuery.refetch()}>
                <RefreshIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Stack>

            {jobsQuery.isLoading ? (
              <Stack spacing={1}>
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} height={48} sx={{ borderRadius: '8px' }} />
                ))}
              </Stack>
            ) : (jobsQuery.data?.jobs || []).length === 0 ? (
              <Typography sx={{ fontSize: 13, color: 'text.disabled', textAlign: 'center', py: 3 }}>
                {t('imageGenerator.history.empty', 'No generation history yet')}
              </Typography>
            ) : (
              <Stack spacing={0.5} sx={{ maxHeight: 400, overflow: 'auto' }}>
                {(jobsQuery.data?.jobs || []).map((job) => (
                  <Box
                    key={job.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: '8px',
                      cursor: job.resultUrl ? 'pointer' : 'default',
                      transition: 'background 0.15s',
                      '&:hover': { bgcolor: alpha(theme.palette.grey[500], 0.06) },
                    }}
                    onClick={() => {
                      if (job.resultUrl) window.open(job.resultUrl, '_blank');
                    }}
                  >
                    {job.resultUrl ? (
                      <Box
                        component="img"
                        src={job.resultUrl}
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '6px',
                          objectFit: 'cover',
                          border: '1px solid',
                          borderColor: 'grey.200',
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '6px',
                          bgcolor: 'grey.100',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <ImageIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                      </Box>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 500,
                          color: 'text.primary',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {job.prompt.substring(0, 60)}{job.prompt.length > 60 ? '...' : ''}
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Chip
                          label={job.imageType}
                          size="small"
                          sx={{ height: 18, fontSize: 10, fontWeight: 600 }}
                        />
                        {job.format && (
                          <Chip
                            label={job.format}
                            size="small"
                            variant="outlined"
                            sx={{ height: 18, fontSize: 10 }}
                          />
                        )}
                        <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>
                          {new Date(job.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      </Stack>
                    </Box>
                    {statusIcon(job.status)}
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>
        </Stack>

        {/* Right column: Results */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: '12px',
            border: '1px solid',
            borderColor: 'grey.200',
            bgcolor: 'background.paper',
            minHeight: 400,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <LayersIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {t('imageGenerator.results.title', 'Generated Images')}
            </Typography>
            {results.length > 0 && (
              <Chip label={results.length} size="small" sx={{ height: 20, fontSize: 11, fontWeight: 600 }} />
            )}
          </Stack>

          {results.length === 0 && !generateMutation.isPending ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 10,
                color: 'text.disabled',
              }}
            >
              <ImageIcon sx={{ fontSize: 48, mb: 2, opacity: 0.3 }} />
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                {t('imageGenerator.results.empty.title', 'No images generated yet')}
              </Typography>
              <Typography sx={{ fontSize: 12, mt: 0.5 }}>
                {t('imageGenerator.results.empty.hint', 'Write a prompt and hit Generate to get started')}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {generateMutation.isPending && (
                <Box
                  sx={{
                    p: 4,
                    borderRadius: '10px',
                    border: '1px dashed',
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <CircularProgress size={32} />
                  <Typography sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 500 }}>
                    {t('imageGenerator.results.generating', 'Generating with multimodal AI...')}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                    {t('imageGenerator.results.generatingHint', 'This typically takes 10-30 seconds')}
                  </Typography>
                </Box>
              )}

              {results.map((result) => (
                <Box
                  key={result.jobId}
                  sx={{
                    borderRadius: '10px',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    overflow: 'hidden',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: 'grey.300',
                      boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.05)}`,
                    },
                  }}
                >
                  {/* Image */}
                  <Box
                    sx={{
                      position: 'relative',
                      bgcolor: 'grey.100',
                      // Checkerboard for transparency
                      backgroundImage:
                        'linear-gradient(45deg, #e0e0e0 25%, transparent 25%), linear-gradient(-45deg, #e0e0e0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e0e0e0 75%), linear-gradient(-45deg, transparent 75%, #e0e0e0 75%)',
                      backgroundSize: '16px 16px',
                      backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
                    }}
                  >
                    <Box
                      component="img"
                      src={result.imageUrl}
                      alt={result.altText || t('imageGenerator.results.imageAlt', 'Generated image')}
                      sx={{
                        width: '100%',
                        maxHeight: 500,
                        objectFit: 'contain',
                        display: 'block',
                      }}
                    />
                  </Box>

                  {/* Info bar */}
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={`${result.width} x ${result.height}`}
                          size="small"
                          sx={{ height: 22, fontSize: 11, fontWeight: 600, bgcolor: 'grey.100' }}
                        />
                        {result.format && (
                          <Chip
                            label={result.format}
                            size="small"
                            variant="outlined"
                            sx={{ height: 22, fontSize: 11 }}
                          />
                        )}
                      </Stack>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title={t('imageGenerator.results.copyUrl', 'Copy URL')}>
                          <IconButton size="small" onClick={() => copyUrl(result.imageUrl)}>
                            <CopyIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('imageGenerator.results.openInNewTab', 'Open in new tab')}>
                          <IconButton
                            size="small"
                            onClick={() => window.open(result.imageUrl, '_blank')}
                          >
                            <OpenInNewIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('imageGenerator.results.download', 'Download')}>
                          <IconButton
                            size="small"
                            component="a"
                            href={result.imageUrl}
                            download
                          >
                            <DownloadIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </Box>
                </Box>
              ))}
            </Stack>
          )}
        </Paper>
      </Box>

      <ProductPickerModal
        open={productPickerOpen}
        onClose={() => setProductPickerOpen(false)}
        onSelect={(products) => {
          setSelectedProducts(products);
          setProductPickerOpen(false);
        }}
        selectedProducts={selectedProducts}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ borderRadius: '8px' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardContent>
  );
}
